import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { POInvoiceReconciliationStatus } from "src/types";

interface ReconciliationResult {
  status?: POInvoiceReconciliationStatus;
  detail?: string;
  brandInvoiceValue?: number;
  brandInvoiceCurrency?: string;
  totalCost?: number;
}

interface ModalProps {
  purchaseOrderId: string;
  onClose?: () => void;
  reconciliation?: ReconciliationResult;
}

interface SuccessProps extends ModalProps {}

interface ErrorProps extends ModalProps {
  onRetry: () => void;
}

const InvoiceValidationBanner: React.FC<{ reconciliation?: ReconciliationResult }> = ({ reconciliation }) => {
  if (!reconciliation?.status) {
    return null;
  }

  const { status, detail } = reconciliation;

  if (status === "MATCH") {
    return (
      <div className="rounded-lg border border-success-200 bg-success-50 p-4">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="w-5 h-5 text-success-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-success-800">Invoice total validated</p>
            <p className="text-sm text-success-700">
              {detail ?? "The invoice total matches the order total."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isMismatch = status === "MISMATCH";
  return (
    <div className={`rounded-lg border p-4 ${isMismatch ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${isMismatch ? "text-red-600" : "text-amber-500"}`} />
        <div>
          <p className={`text-sm font-semibold ${isMismatch ? "text-red-800" : "text-amber-800"}`}>
            {isMismatch ? "Invoice total doesn't match the order" : "Invoice total needs review"}
          </p>
          <p className={`text-sm ${isMismatch ? "text-red-700" : "text-amber-700"}`}>
            {detail ??
              (isMismatch
                ? "The invoice total does not match the order total — please double-check before submitting."
                : "We couldn't automatically validate the invoice total — it has been flagged for manual review.")}
          </p>
        </div>
      </div>
    </div>
  );
};

const Success: React.FC<SuccessProps> = ({ purchaseOrderId, onClose, reconciliation }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900">Invoice Updated</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <InvoiceValidationBanner reconciliation={reconciliation} />

          <div className="bg-success-50 border border-success-200 rounded-lg p-4 space-y-2">
            <p className="text-sm text-gray-700">Thank you for your submission</p>
            <p className="text-sm text-gray-700">
              The purchase order invoice details have been updated successfully{" "}
              <span className="font-semibold text-success-700">(ID: {purchaseOrderId})</span>
            </p>
            <p className="text-sm text-gray-700">We look forward to future collaboration</p>
          </div>

          {/* Home Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.home.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const Error: React.FC<ErrorProps> = ({ onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">Update Failed</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5">
          <p className="text-sm text-gray-700 text-center mb-6">
            An error occurred while updating the invoice details. Please try again.
          </p>

          {/* Footer Button */}
          <div className="flex justify-center">
            <button
              className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
              onClick={onRetry}
            >
              Retry Update
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UpdatePurchaseOrderInvoiceModals = {
  Success,
  Error
};

export { UpdatePurchaseOrderInvoiceModals };
